import React, { useState } from 'react';

import moment from 'moment-timezone';
import { useRecoilValue } from 'recoil';

import { cn, formatDateNative, ia } from 'lib/helpers/utility';

import { currentPractice } from 'components/practice/practiceState';

import Button from '../Button/Button';
import Calendar from '../Calendar/Calendar';
import Icon from '../Icon/Icon';
import Input from '../Input/Input';
import Popover from '../Popovers/Popover/Popover';
import Typography from '../Typography/Typography';

export function getDateRange(unit, amount, timeZone) {
  const now = moment.tz(timeZone);
  const startDate = now.clone().subtract(amount, unit).startOf('day').toISOString();
  const endDate = now.clone().endOf('day').toISOString();
  return { startDate, endDate };
}

export function getDateRangeByUnit(unit, amount, timeZone) {
  const now = moment.tz(timeZone);
  const startDate = now.clone().startOf(unit).subtract(amount, unit).toISOString();
  const endDate = now.clone().endOf(unit).subtract(amount, unit).toISOString();
  return { startDate, endDate };
}

export function getUtcRangeForDate(dateStr, timeZone) {
  const localDate = moment.tz(dateStr, 'MM/DD/YYYY', timeZone);
  const startDate = localDate.clone().startOf('day').toISOString();
  const endDate = localDate.clone().endOf('day').toISOString();
  return { startDate, endDate };
}

export const getDateOptions = (timeZone) => [
  { id: 'all-time', label: 'All Time', value: null },
  { id: 'today', label: 'Today', value: getDateRangeByUnit('days', 0, timeZone) },
  { id: 'yesterday', label: 'Yesterday', value: getDateRangeByUnit('days', 1, timeZone) },
  { id: 'last-week', label: 'Last Week', value: getDateRangeByUnit('weeks', 1, timeZone) },
  { id: 'last-30-days', label: 'Last 30 Days', value: getDateRange('days', 30, timeZone) },
  { id: 'last-60-days', label: 'Last 60 Days', value: getDateRange('days', 60, timeZone) },
  { id: 'last-90-days', label: 'Last 90 Days', value: getDateRange('days', 90, timeZone) },
  {
    id: 'custom-date-range',
    label: 'Custom date range',
    value: null
  }
];

const Option = ({ label, value, onClick, option, selectedOption }) => {
  return (
    <div
      className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50"
      onClick={() => onClick(option)}>
      <Typography className="cursor-pointer whitespace-nowrap">{label}</Typography>
      {selectedOption?.value === value && <Icon icon="check-linear" color="neutral" shadow={700} />}
    </div>
  );
};

export const DateRangeContent = ({
  onChange,
  value,
  onClose = () => {},
  className,
  dataKey,
  formula
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const practice = useRecoilValue(currentPractice);
  const timezone = practice?.timezone;

  const handleChange = (event) => {
    const value = event.target.value;
    setInput(value);

    const number = parseInt(value, 10);

    if (!isNaN(number)) {
      setSuggestions([
        {
          label: number === 1 ? 'Yesterday' : `Last ${number} Days`,
          value: getDateRange('days', number)
        },
        {
          label: number === 1 ? 'Last Week' : `Last ${number} Weeks`,
          value: getDateRange('weeks', number)
        },
        {
          label: number === 1 ? 'Last Month' : `Last ${number} Months`,
          value: getDateRange('months', number)
        }
      ]);
    } else {
      setSuggestions([]);
    }
  };

  const onSelectOptionHandler = (option, closePopover) => {
    if (option.id !== 'custom-date-range') {
      setInput('');
      setSuggestions([]);
      setIsOpen(false);
      if (!option.value) {
        onChange(
          { id: option.id, startDate: null, endDate: null, timezone, label: option.label },
          dataKey
        );
        closePopover();
        return;
      }
      onChange({ id: option.id, ...option.value, label: option.label, timezone }, dataKey, formula);
      closePopover();
      return;
    }
    setIsOpen(true);
  };

  const onRangeChange = (ranges) => {
    const [start, end] = ranges;

    const startDate = formatDateNative(start);
    const endDate = formatDateNative(end);

    onChange({ id: 'custom-date-range', startDate, endDate, timezone }, dataKey, formula);
    onClose();
  };

  const toRenderOptions = ia(suggestions) ? suggestions : getDateOptions(timezone);

  return (
    <div className={className}>
      {!isOpen ? (
        <div className="flex flex-col gap-y-2">
          <Input
            inputWrapperClassName="w-full border-0 border-b border-neutral-200 rounded-none"
            placeholder="Custom range: 2d, 4d, 8weeks..."
            value={input}
            onChange={handleChange}
          />
          {toRenderOptions.map((option) => (
            <Option
              key={option.id}
              label={option.label}
              value={option.value}
              onClick={() => onSelectOptionHandler(option, onClose)}
              selectedOption={value}
            />
          ))}
        </div>
      ) : (
        <Calendar
          format="MM/DD/YYYY"
          onChange={onRangeChange}
          value={[value?.startDate, value?.endDate]}
          isRange
        />
      )}
    </div>
  );
};

const DateRangeSelector = ({ onChange, value, buttonClassName, buttonIcon, position }) => {
  const [isOpen, setIsOpen] = useState(false);
  const practice = useRecoilValue(currentPractice);
  const timezone = practice?.timezone;

  const buttonText =
    value?.id !== 'custom-date-range' ? (
      getDateOptions(timezone).find((option) => option.id === value?.id)?.label ||
      value?.label ||
      'All Time'
    ) : (
      <>
        <label className="cursor-pointer text-neutral-800">
          {formatDateNative(value?.startDate)}
        </label>
        <span className="cursor-pointer text-neutral-800">-</span>
        <label className="cursor-pointer text-neutral-800">
          {formatDateNative(value?.endDate)}
        </label>
      </>
    );

  return (
    <Popover
      panelClassName="p-0 min-w-[244px] w-fit"
      position={position}
      isFixed
      customButtonTrigger={({ open }) => (
        <Button
          variant="tertiary"
          size="xs"
          text={buttonText}
          icon={buttonIcon}
          className={cn('whitespace-nowrap', open && 'bg-neutral-100', buttonClassName)}
          onClick={() => setIsOpen(false)}
          iconColor="neutral"
        />
      )}>
      {({ closePopover }) => (
        <DateRangeContent
          isOpen={isOpen}
          onChange={onChange}
          value={value}
          onClose={closePopover}
        />
      )}
    </Popover>
  );
};

export default DateRangeSelector;
